import React, { useEffect, useMemo, useState } from "react";
import { match, P } from "ts-pattern";
import { useNavigate } from "react-router-dom";
import * as t from "io-ts";
import { styled } from "styled-components";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Tooltip2 } from "@blueprintjs/popover2";

import {
  Button,
  ButtonGroup,
  Colors,
  Position,
  Pre,
  Tab,
  Tabs,
} from "@blueprintjs/core";
import {
  Box,
  CenterSpinner,
  CustomCard,
  ErrorState,
  NoData,
  PageComponent,
  PrettyJson,
  TitleComponent,
  useToggle,
  JsonDiffModalButton,
} from "ui-components";
import { goToTask } from "./go-to-task";
import { taskQuery, TASKS_QUERY_FILTER } from "ui-io/queries/tasks/task-query";
import { getFundingRoundsById } from "ui-io/queries/get-funding-rounds-by-id";
import { getFinancialsById } from "ui-io/queries/get-financials-by-id";
import DiscussionThread from "../../components/task/discussion-thread";
import CompanyDetailDisplay from "../../components/company-detail-display";
import { getAdminCompanyById } from "ui-io/queries/get-admin-company-by-id";
import { getCompanyConfig } from "ui-io/queries/get-company-config";
import { ConfigT } from "ui-io/config-io";
import { TaskT } from "ui-io/task-io/task-t";
import { NoteToAddT, RawTaskT, TodoNoteT } from "ui-io/task-io";
import { CompanyT } from "../company/table";
import CompanyNotes from "../../components/company-notes";
import { getBrandNotes } from "ui-io/queries/configs/get-brand-notes";
import TaskHeader from "./task-header";
import { CompanyType, FinancialT, FundingRoundT } from "ui-io/company-io";
import { taskEditMutation } from "ui-io/queries/tasks/task-edit-mutation";
import toasts from "../../utils/toasts";
import { InboxViewT } from "../tasks/inbox-views";

export const TaskPageSubTabIdIO = t.keyof({
  company: null,
  taskJson: null,
  notes: null,
  config: null,
  fundingRounds: null,
  financials: null,
});

type TaskPageProps = {
  taskId: string;
  tabId?: InboxViewT;
  subtabId?: string;
};

const TaskPage = ({ taskId, tabId = "Inbox", subtabId }: TaskPageProps) => {
  const history = useNavigate();
  const configSectionRef = React.useRef<HTMLDivElement>(null);
  const [hasNewEditInTaskJson, setHasNewEditInTaskJson] = useState(false);
  const [fullContentView, toggleFullContentView] = useToggle(false);
  // Configuration state
  const [configJson, setConfigJson] = useState<ConfigT>({} as ConfigT);
  const [companyConfig, setCompanyConfig] = useState({});

  const [companyFundingRounds, setCompanyFundingRounds] = useState<
    FundingRoundT[]
  >([]);

  const [companyFinancials, setCompanyFinancials] = useState<FinancialT[]>([]);

  // Company state
  const [companyJson, setCompanyJson] = useState<CompanyType>(
    {} as CompanyType,
  );
  const [companyDetail, setCompanyDetail] = useState({} as CompanyType);
  const [prevCompanyDetail, setPrevCompanyDetail] = useState({} as CompanyType);

  const [taskJson, setTaskJson] = useState<RawTaskT>({} as RawTaskT);
  const taskConnection = useQuery({
    ...taskQuery({ taskId }),
    enabled: !!taskId,
  });
  const fundingRoundsConnection = useQuery({
    ...getFundingRoundsById({ companyID: taskConnection.data?.companyID }),
    enabled: !!taskConnection.data?.companyID,
  });
  const financialsConnection = useQuery({
    ...getFinancialsById({ companyID: taskConnection.data?.companyID }),
    enabled: !!taskConnection.data?.companyID,
  });
  useEffect(() => {
    if (!taskConnection.data?._raw) return;
    setTaskJson(taskConnection?.data?._raw ?? ({} as RawTaskT));

    if (taskConnection.data?._raw?.configuration) {
      setConfigJson(taskConnection.data?._raw.configuration);
      setCompanyConfig(taskConnection.data._raw?.configuration);
    }

    if (taskConnection.data?._raw.companyDetail) {
      setCompanyDetail(taskConnection.data._raw.companyDetail);
      setCompanyJson(taskConnection.data._raw.companyDetail);
    }
    if (taskConnection.data?._raw.oldCompanyDetail) {
      setPrevCompanyDetail(taskConnection.data._raw.oldCompanyDetail);
    }
  }, [taskConnection.data, taskConnection.isSuccess]);
  useEffect(() => {
    if (companyDetail.funding_rounds?.length) {
      setCompanyFundingRounds(companyDetail.funding_rounds);
    } else if (
      fundingRoundsConnection.isSuccess &&
      fundingRoundsConnection.data?.length
    ) {
      setCompanyFundingRounds(fundingRoundsConnection.data);
    }
  }, [fundingRoundsConnection.isSuccess, companyDetail.funding_rounds]);

  useEffect(() => {
    if (companyDetail.financials?.length) {
      setCompanyFinancials(companyDetail.financials);
    } else if (
      financialsConnection.isSuccess &&
      financialsConnection.data?.length
    ) {
      setCompanyFinancials(financialsConnection.data ?? []);
    }
  }, [financialsConnection.isSuccess, companyDetail.financials]);

  useEffect(() => {
    const isCompanyEqual =
      JSON.stringify(companyDetail ?? {}) === JSON.stringify(companyJson ?? {});
    const isFinancialEqual =
      JSON.stringify(companyJson.financials ?? []) ===
      JSON.stringify(companyFinancials ?? []);
    const isFundingRoundEqual =
      JSON.stringify(companyJson.funding_rounds ?? []) ===
      JSON.stringify(companyFundingRounds ?? []);

    setHasNewEditInTaskJson(
      !isCompanyEqual || !isFinancialEqual || !isFundingRoundEqual,
    );
  }, [
    configJson,
    companyJson,
    companyDetail,
    companyConfig,
    companyFinancials,
    companyFundingRounds,
  ]);

  const handleTaskJsonEdit = (updated: RawTaskT) => {
    setTaskJson(updated);
  };
  const handleCompanyJsonEdit = (updated: typeof companyJson) => {
    setCompanyJson(updated);
  };
  const handleFundingRoundsJsonEdit = (
    updated: typeof companyFundingRounds,
  ) => {
    setCompanyFundingRounds(updated);
  };
  const handleFinancialsJsonEdit = (updated: typeof companyFinancials) => {
    setCompanyFinancials(updated);
  };
  const queryClient = useQueryClient();
  const mutation = useMutation(taskEditMutation, {
    onSuccess: () => {
      toasts.success(`Task updated successfully`);
      queryClient.refetchQueries([TASKS_QUERY_FILTER]);
    },
    onError: (err) => {
      toasts.error("Error in updating task");
    },
  });

  const handleSaveEditedTask = () => {
    const payload = {
      configuration: configJson,
      companyDetail: {
        ...companyJson,
        funding_rounds: companyFundingRounds,
        financials: companyFinancials,
      } as CompanyType,
    };
    mutation.mutate({
      taskId,
      payload: payload,
    });
  };

  const companyNotes = useQuery({
    ...getBrandNotes({ companyID: taskConnection.data?.companyID || "" }),
    enabled: !!taskConnection.data?.companyID,
  });

  const brandNotes = useMemo(() => {
    return companyNotes.data?.filter((note: TodoNoteT) => note.isPinned) || [];
  }, [companyNotes.data]);
  if (taskConnection.isLoading || taskConnection.isIdle) {
    return <CenterSpinner />;
  }
  if (!taskConnection.data) {
    return (
      <NoData
        icon="warning-sign"
        title="Task not found"
        description="A task with the specified task ID could not be found"
      />
    );
  }
  return (
    <>
      <PageComponent fill overflowY="hidden" padTop="xxsmall">
        {/* <TaskHeader task={taskConnection.data} prevInboxView={"Inbox"} /> */}
        <TaskHeader
          isLoading={taskConnection.isLoading}
          isError={taskConnection.isError}
          task={taskConnection.data ?? ({} as TaskT)}
          view={tabId}
        />
        <MainContent>
          <Section style={{ flex: "1 1 450px", maxWidth: 700, height: 700 }}>
            {match(taskConnection)
              .with({ status: "error" }, ({ error }) => (
                <ErrorState error={error} />
              ))
              .with({ status: "success" }, ({ data }) => (
                <>
                  <DiscussionThread preloadedTask={data} />
                </>
              ))
              .exhaustive()}
          </Section>
          <Section style={{ flex: "1 99999 900px" }}>
            <ConfigurationHeader>
              <Box gap="small">
                <TitleComponent marginTop="0px" fontSize="18px">
                  Configuration & Details
                </TitleComponent>
              </Box>
              <Box>
                <ButtonGroup style={{ gap: "5px" }}>
                  {(tabId == "InProgress" || tabId == "InReview") && (
                    <Tooltip2
                      hoverOpenDelay={500}
                      content="Save changes"
                      position={Position.TOP_LEFT}
                    >
                      <Button
                        active={hasNewEditInTaskJson}
                        intent={`${hasNewEditInTaskJson ? "primary" : "none"}`}
                        icon={"tick"}
                        loading={mutation.isLoading}
                        onClick={handleSaveEditedTask}
                        disabled={!hasNewEditInTaskJson}
                      />
                    </Tooltip2>
                  )}

                  <Tooltip2
                    hoverOpenDelay={500}
                    content="See the difference between the current and previous configuration"
                    position={Position.TOP_LEFT}
                  >
                    <JsonDiffModalButton
                      currentValue={companyJson ?? {}}
                      previousValue={prevCompanyDetail ?? {}}
                      minimal={false}
                    />
                  </Tooltip2>
                  <Tooltip2 content="Full content view" hoverOpenDelay={500}>
                    <Button
                      active={fullContentView}
                      intent={fullContentView ? "primary" : "none"}
                      icon={fullContentView ? "collapse-all" : "expand-all"}
                      onClick={toggleFullContentView}
                    />
                  </Tooltip2>
                </ButtonGroup>
              </Box>
            </ConfigurationHeader>

            <DetailsBox visible={fullContentView}>
              <DetailsContent>
                {!!companyDetail ? (
                  <CompanyDetailDisplay
                    companyJson={companyJson}
                    setCompanyJson={setCompanyJson}
                  />
                ) : (
                  <NoData
                    description="Please verify the company JSON"
                    translateY={5}
                  />
                )}
              </DetailsContent>
            </DetailsBox>
            <ConfigurationSection ref={configSectionRef}>
              <Tabs
                renderActiveTabPanelOnly
                selectedTabId={subtabId || "company"}
                onChange={(subTabId) => {
                  if (!TaskPageSubTabIdIO.is(subTabId)) {
                    return;
                  }
                  if (!subTabId || subTabId === "company") {
                    goToTask(history, {
                      taskId,
                      tabId,
                      subtabId: "company",
                    });
                  } else {
                    goToTask(history, {
                      taskId,
                      tabId,
                      subtabId: subTabId,
                    });
                  }
                }}
              >
                <Tab
                  id="company"
                  title="Company"
                  panel={(() => {
                    // Destructure and filter out the financials and funding_rounds key inline
                    const { financials, funding_rounds, ...companyCleaned } =
                      companyJson ?? {};

                    return (
                      <PrettyJson
                        onEdit={handleCompanyJsonEdit}
                        collapsed={1}
                        src={{ ...companyCleaned }}
                      />
                    );
                  })()}
                />
                <Tab
                  id="fundingRounds"
                  title="Funding Rounds"
                  panel={
                    <PrettyJson
                      onEdit={handleFundingRoundsJsonEdit}
                      isDelete={true}
                      collapsed={1}
                      src={companyFundingRounds}
                    />
                  }
                />
                <Tab
                  id="financials"
                  title="Financials"
                  panel={
                    <PrettyJson
                      onEdit={handleFinancialsJsonEdit}
                      isDelete={true}
                      collapsed={1}
                      src={companyFinancials}
                    />
                  }
                />
                <Tab
                  id="config"
                  title="Config"
                  panel={<PrettyJson collapsed={1} src={companyConfig ?? {}} />}
                />
                <Tab
                  id="taskJson"
                  title="Task JSON"
                  panel={
                    <PrettyJson
                      collapsed={1}
                      onEdit={(v: any) => handleTaskJsonEdit(v as RawTaskT)}
                      src={taskConnection.data?._raw ?? {}}
                    />
                  }
                />

                {brandNotes.length ? (
                  <Tab
                    id="notes"
                    title={`Notes (${brandNotes?.length})`}
                    panel={
                      <CompanyNotes companyID={taskConnection.data.companyID} />
                    }
                  />
                ) : null}
              </Tabs>
            </ConfigurationSection>
          </Section>
        </MainContent>
      </PageComponent>
    </>
  );
};
export default TaskPage;

const MainContent = styled.main`
  display: flex;
  flex: 1;
  padding: 2px;
  overflow: hidden;
  gap: 20px;
  box-shadow: none;
  align-items: flex-start;
`;

const Section = styled(CustomCard)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  min-height: 700px;
  margin-top: 0px;
`;

const ConfigurationHeader = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: sticky;
  top: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => {
    return props.theme.darkMode ? Colors.LIGHT_GRAY5 : Colors.DARK_GRAY2;
  }};
`;

const DetailsBox = styled.div<{ visible: boolean }>`
  overflow: hidden;
  max-height: ${({ visible }) => (visible ? "1000px" : "0")};
  transition: max-height 0.5s ease-in-out;
  transition-delay: ${({ visible }) => (visible ? "0.3s" : "0s")};
  border-top: ${({ visible, theme: { darkMode } }) =>
    visible
      ? `1px solid ${darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY1}`
      : "unset"};
`;
const DetailsContent = styled.div`
  margin-left: 20px;
  margin-bottom: 40px;
  transition: none;
`;

const ConfigurationSection = styled.div`
  overflow-y: auto;
  padding: 20px;
  border-top-style: solid;
  border-top-width: 1px;
  border-top-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY5 : Colors.LIGHT_GRAY1;
  }};
`;
